import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';

import { loadContacts, createContact, updateContact, deleteContact, restoreContact } from './api';

const contactsAdapter = createEntityAdapter({
  selectId: contact => contact.id,
});

const contactsSlice = createSlice({
  name: 'contacts',
  initialState: contactsAdapter.getInitialState({
    contactsByFilter: {}, // Store contacts by filter key
    loading: false,
    error: null,
  }),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadContacts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadContacts.fulfilled, (state, action) => {
        state.loading = false;
        contactsAdapter.upsertMany(state, action.payload.data);
        // Store contact IDs by filter key
        state.contactsByFilter[action.payload.filterKey] = action.payload.data.map(c => c.id);
      })
      .addCase(loadContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        // Add new contact to state
        contactsAdapter.addOne(state, action.payload);

        // Add to relevant contactsByFilter arrays so it shows in address book immediately
        const newContact = action.payload;
        Object.keys(state.contactsByFilter).forEach(filterKey => {
          try {
            const filter = JSON.parse(filterKey);
            // Check if this filter matches the new contact's customer/site_group
            const matchesCustomer = filter.customer && filter.customer === newContact.customer;
            const matchesSiteGroup = filter.site_group && filter.site_group === newContact.site_group;
            if (matchesCustomer || matchesSiteGroup) {
              // Add to beginning of array (most recent first)
              if (!state.contactsByFilter[filterKey].includes(newContact.id)) {
                state.contactsByFilter[filterKey].unshift(newContact.id);
              }
            }
          } catch {
            // Skip invalid filter keys
          }
        });
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        // Update existing contact in state
        contactsAdapter.upsertOne(state, action.payload);
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        // Soft delete - remove from state (since non-staff can't see deleted)
        contactsAdapter.removeOne(state, action.payload);

        // Remove from all contactsByFilter arrays
        const deletedId = action.payload;
        Object.keys(state.contactsByFilter).forEach(filterKey => {
          const index = state.contactsByFilter[filterKey].indexOf(deletedId);
          if (index > -1) {
            state.contactsByFilter[filterKey].splice(index, 1);
          }
        });
      })
      .addCase(restoreContact.fulfilled, (state, action) => {
        contactsAdapter.upsertOne(state, action.payload);

        // Add restored contact back to relevant contactsByFilter arrays
        const restoredContact = action.payload;
        Object.keys(state.contactsByFilter).forEach(filterKey => {
          try {
            const filter = JSON.parse(filterKey);
            const matchesCustomer = filter.customer && filter.customer === restoredContact.customer;
            const matchesSiteGroup = filter.site_group && filter.site_group === restoredContact.site_group;
            if (matchesCustomer || matchesSiteGroup) {
              if (!state.contactsByFilter[filterKey].includes(restoredContact.id)) {
                state.contactsByFilter[filterKey].unshift(restoredContact.id);
              }
            }
          } catch {
            // Skip invalid filter keys
          }
        });
      });
  },
});

const contactsSelectors = contactsAdapter.getSelectors(state => state.contacts);

export const selectContactsByFilter = (state, filterKey) => {
  const contactIds = state.contacts.contactsByFilter[filterKey] || [];
  return contactIds.map(id => contactsSelectors.selectById(state, id)).filter(Boolean);
};

export default contactsSlice.reducer;
